'use client'
import Button from '@lyra/core/components/Button'
import ButtonGroup from '@lyra/core/components/ButtonGroup'
import Section, { SectionProps } from '@lyra/core/components/Section'
import { Order } from '@lyra/web/constants/order'
import { PageId } from '@lyra/web/constants/pages'
import { QueryParam, QueryPositionTab } from '@lyra/web/constants/query'
import BalancesTable from '@lyra/web/containers/common/BalancesTable'
import useAuth from '@lyra/web/hooks/useAuth'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import GreeksTable from '@lyra/web/src/components/common/GreeksTable'
import PositionsTable from '@lyra/web/src/components/common/PositionsTable'
import QuotesTable from '@lyra/web/src/containers/common/QuotesTable'
import { getPagePath } from '@lyra/web/utils/pages'
import { sortPositionsOrOpenOrders } from '@lyra/web/utils/positions'
import { useRouter } from 'next/navigation'
import { parseAsStringEnum, useQueryState } from 'next-usequerystate'
import { useMemo } from 'react'

import OpenOrdersTable from './OpenOrdersTable'
import SubaccountsDropdown from './SubaccountsDropdown'
import SubaccountSummaryItems from './SubaccountSummaryItems'

type Props = {
  isCompact?: boolean
  showSubaccountSwitch?: boolean
  showSubaccountSummary?: boolean
  showQuotesTab?: boolean
  pageSize?: number
} & SectionProps

const PositionsSection = ({
  isCompact,
  showSubaccountSwitch,
  showSubaccountSummary,
  showQuotesTab,
  pageSize,
  ...sectionProps
}: Props) => {
  const queryValues = parseAsStringEnum(Object.values(QueryPositionTab))
  const [tab, onChangeTab] = useQueryState(
    QueryParam.PositionTab,
    queryValues.withDefault(QueryPositionTab.Positions)
  )

  const { isAuthenticated } = useAuth()
  const { subaccount, subaccounts, subaccountData, switchSubaccount } = useSubaccount()

  const positions = useMemo(() => {
    const positions = subaccount ? subaccount.positions.filter((pos) => pos.amount !== '0') : []
    return sortPositionsOrOpenOrders(positions)
  }, [subaccount])

  const openOrders: Order[] = useMemo(() => {
    return subaccount ? (sortPositionsOrOpenOrders(subaccount.open_orders) as Order[]) : []
  }, [subaccount])

  const subaccountSummaryRow =
    showSubaccountSummary && subaccount ? <SubaccountSummaryItems isCompact={isCompact} /> : null

  const subaccountSwitch =
    showSubaccountSwitch && subaccount && Object.values(subaccounts).length > 1 ? (
      <SubaccountsDropdown
        subaccountId={subaccount.subaccount_id}
        onSelect={switchSubaccount}
        placement={!isCompact ? 'bottom-end' : 'top-start'}
      />
    ) : null

  const router = useRouter()

  return (
    <Section {...sectionProps}>
      {!isCompact ? (
        <>
          <Section.Header
            onPressTitle={() => router.push(getPagePath({ page: PageId.Positions }))}
            title="Positions"
            rightContent={subaccountSwitch}
          />
          {subaccountSummaryRow ? <Section.XStack>{subaccountSummaryRow}</Section.XStack> : null}
        </>
      ) : null}
      <Section.XStack flexWrap="wrap" width="100%" alignItems="center">
        <ButtonGroup>
          <Button
            isSelected={tab === QueryPositionTab.Positions}
            onPress={(e) => {
              e.stopPropagation()
              onChangeTab(QueryPositionTab.Positions)
            }}
            label={'POSITIONS'}
          />
          <Button
            isSelected={tab === QueryPositionTab.Orders}
            onPress={(e) => {
              e.stopPropagation()
              onChangeTab(QueryPositionTab.Orders)
            }}
            label={`OPEN ORDERS${openOrders.length > 0 ? ` (${openOrders.length})` : ''}`}
          />
          <Button
            isSelected={tab === QueryPositionTab.Balances}
            onPress={(e) => {
              e.stopPropagation()
              onChangeTab(QueryPositionTab.Balances)
            }}
            label="BALANCES"
          />
          {positions.length ? (
            <Button
              isSelected={tab === QueryPositionTab.Greeks}
              onPress={(e) => {
                e.stopPropagation()
                onChangeTab(QueryPositionTab.Greeks)
              }}
              label="GREEKS"
            />
          ) : null}
          {showQuotesTab ? (
            <Button
              isSelected={tab === QueryPositionTab.Quotes}
              onPress={(e) => {
                e.stopPropagation()
                onChangeTab(QueryPositionTab.Quotes)
              }}
              label="Quotes"
            />
          ) : null}
        </ButtonGroup>
        {/* compact, show inline with button group */}
        {isCompact ? subaccountSwitch : null}
        {isCompact ? subaccountSummaryRow : null}
      </Section.XStack>
      {tab === QueryPositionTab.Positions ? (
        <Section.Table>
          <PositionsTable
            emptyMessage={!isAuthenticated ? 'Not connected' : 'No open positions'}
            subaccount={subaccountData}
            pageSize={pageSize}
            showActions
          />
        </Section.Table>
      ) : tab === QueryPositionTab.Orders ? (
        <Section.Table>
          <OpenOrdersTable
            emptyMessage={!isAuthenticated ? 'Not connected' : 'No open orders'}
            orders={openOrders}
            pageSize={pageSize}
          />
        </Section.Table>
      ) : tab === QueryPositionTab.Greeks ? (
        <Section.Table>
          <GreeksTable
            emptyMessage={!isAuthenticated ? 'Not connected' : 'No open positions'}
            positions={positions}
          />
        </Section.Table>
      ) : tab === QueryPositionTab.Balances ? (
        <Section.Table>
          <BalancesTable
            emptyMessage={!isAuthenticated ? 'Not connected' : 'No balances'}
            pageSize={pageSize}
            subaccount={subaccountData}
            showActions
            showApy
          />
        </Section.Table>
      ) : tab === QueryPositionTab.Quotes ? (
        <Section.Table>
          <QuotesTable pageSize={pageSize} />
        </Section.Table>
      ) : null}
    </Section>
  )
}

export default PositionsSection
